import { useState } from 'react';
import { format, isBefore, isSameDay, differenceInCalendarDays, addWeeks } from 'date-fns';
import ja from 'date-fns/locale/ja';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getCalendarStartingDate, getDatesOfOneWeek } from 'src/utils/calendar';

type Props = {
  currentDate: Date;
  initialSelectedDate: Date | undefined;
  onDateClicked: (dateText: string) => void; // dateTextは ISO8601の日付だけ yyyy-MM-dd
};

// 横スワイプカレンダー(曜日は月曜はじまり)
export const HorizontalScrollingCalendar = ({ currentDate, initialSelectedDate, onDateClicked }: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date>(
    initialSelectedDate != null ? initialSelectedDate : new Date(currentDate.getTime()),
  );

  const onClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const newSelectedDateText = e.currentTarget.id;
    setSelectedDate(new Date(newSelectedDateText));
    onDateClicked(newSelectedDateText);
  };

  const getClassNameOfDate = (date: Date, currentDate: Date, selectedDate: Date): string => {
    if (isBefore(date, currentDate)) {
      return 'past-day';
    } else if (isSameDay(date, selectedDate)) {
      return 'selected-day';
    } else {
      return 'display-day';
    }
  };

  /**
   * 横スクロールカレンダーの中身を1週間ずつ1年分(54週)作成する
   * @param calendarStartingDate カレンダー生成の起算日 (現状、月曜日)
   */
  const createSwiperContents = (calendarStartingDate: Date, currentDate: Date, selectedDate: Date) => {
    const swiperContents = [];
    for (let week = 0; week < 54; week++) {
      const mondayOfTheWeek = addWeeks(calendarStartingDate, week);
      const dates = getDatesOfOneWeek(mondayOfTheWeek);
      swiperContents.push(
        <SwiperSlide className="day-clicked" key={`swipeSlide${week}`}>
          {dates.map((date, index) => (
            <input
              type="button"
              className={getClassNameOfDate(date, currentDate, selectedDate)}
              id={format(date, 'yyyy-MM-dd')}
              key={index}
              value={date.getDate()}
              onClick={onClick}
              disabled={isBefore(date, currentDate)} // 過去の日付の場合、クリックさせない
            />
          ))}
        </SwiperSlide>,
      );
    }
    return swiperContents;
  };
  const calendarStartingDate = getCalendarStartingDate(currentDate);
  const swiperContents = createSwiperContents(calendarStartingDate, currentDate, selectedDate);

  const initialSlide =
    initialSelectedDate != null
      ? Math.floor(differenceInCalendarDays(initialSelectedDate, calendarStartingDate) / 7)
      : 0;

  return (
    <>
      <div className="mt-0 mb-3.5 mx-[30px]">
        <div className="mb-5">
          <div className="mt-0 mb-2 mx-[7px] flex justify-between">
            <div className="font-xs text-center">月</div>
            <div className="font-xs text-center">火</div>
            <div className="font-xs text-center">水</div>
            <div className="font-xs text-center">木</div>
            <div className="font-xs text-center">金</div>
            <div className="font-xs text-center text-[#5aa6e5]">土</div>
            <div className="font-xs text-center text-red">日</div>
          </div>

          <Swiper initialSlide={initialSlide} spaceBetween={10} slidesPerView={1}>
            {swiperContents}
          </Swiper>
        </div>

        <div className="text-center">
          <div>{format(selectedDate, 'yyyy年M月d日(E)', { locale: ja })}</div>
        </div>
        <style jsx>{`
          .past-day {
            color: #bbbbbb;
          }
        `}</style>
      </div>
    </>
  );
};
